import React, { useState } from "react";

/* ─────────────────────────────────────────
   DEFAULT METHODOLOGY (Shotgun pipeline)
───────────────────────────────────────── */
const DEFAULT_METHODOLOGY = `
DNA Extraction and Quality Check
Genomic DNA was extracted using the ONEMag™ Rapid Soil DNA Extraction Kit. Briefly, approximately 250 mg of soil sample was processed by adding 800 µL of lysis buffer and homogenizing thoroughly for about 5 minutes. The homogenized mixture was incubated at 65°C for 10 minutes, followed by vortexing and centrifugation at 8000 rpm for 8 minutes. The supernatant was carefully transferred to a fresh tube, and an equal volume of binding buffer was added and incubated for 5 minutes. Subsequently, 20 µL of magnetic silica beads per 1 mL of solution was added and mixed thoroughly to facilitate DNA binding. The bound DNA was washed twice with 500 µL of wash buffer and finally eluted in 50 µL of elution buffer. The purified DNA was then stored appropriately.

Library Preparation and Sequencing
Qualified DNA samples were taken for Illumina library preparation using TruSeq DNA PCRFree Library Prep Kit (Illumina, Inc., USA) where the genomic DNA is fragmented, end repaired, A-tailed, size selected and ligated with Illumina sequencing barcoded adapters. Concentration of prepared library was checked using QuantStudio Real-Time PCR System (Applied Biosystems, USA). After dilution to 1 ng/ul, the Agilent 2100 bioanalyzer (Agilent Technologies, Inc., USA) was used to assess the insert size. Sequencing was carried out at 2 × 150 bp in Illumina NovaSeq X platform (Illumina, Inc., USA).

Bioinformatic Analysis
The reads obtained after sequencing were first QC checked with the tool FastQC v0.10.0. The reads were then trimmed to remove adapters with the tool Trimgalore v0.6.10 using default parameters. The tool Kraken v2 was used to assign taxonomy and calculate the kingdom-wise relative abundances based on the NCBI RefSeq database. The Kraken reports were then converted to biom format using the KrakenTools v1.2.1. The obtained biom file was imported to R studio and used as a physeq object using the Phyloseq package v1.52.0. The kingdom-wise taxonomy abundance plots were visualized using the R package ggplot2 v3.5.1. Alpha diversity is applied in analyzing complexity of species diversity for each sample through indices, including Observed species, evenness, Shannon, etc. using the R package Vegan v2.6-6 and visualized using ggplot2. The Krona chart of the microbiome distribution for each kingdom were generated with the help of Krona Tools v2.8.1 with the Kraken report as input. The Sankey plots were generated using the Pavian R package v1.2.1 with the same Kraken2 reports as input. The clean reads were further assembled using the Megahit assembler v1.2.9 and the gene prediction was carried out using Prodigal v2.6.3. Further, for functional annotation of the genes, EggNOG-mapper v2.1.14 was used with orthology predictions. The KEGG and COG annotation information from the results produced were visualized using ggplot2 in R. Metagenomic binning was carried out using three independent tools, namely MetaBAT, MaxBin2, and SemiBin2, to improve binning accuracy and genome recovery. The resulting bins from each method were subsequently assessed for genome quality using CheckM2. Based on these evaluations, bins exhibiting high completeness and acceptable quality metrics were selected and retained as the final set of reconstructed genomes.`;

/* ─────────────────────────────────────────
   DYNAMIC THEME STYLES
───────────────────────────────────────── */

function injectStyle(dark) {
  let s = document.getElementById("shotgun-style");
  if (!s) {
    s = document.createElement("style");
    s.id = "shotgun-style";
    document.head.appendChild(s);
  }
  s.textContent = getStyle(dark);
}

function getStyle(dark) {
  const bg = dark ? '#0a1628' : '#f4f6f8';
  const cardBg = dark ? '#0f2040' : '#fff';
  const cardBorder = dark ? 'rgba(255,255,255,0.08)' : '#e5e7eb';
  const text = dark ? '#e8edf4' : '#111827';
  const textSub = dark ? '#8aaac8' : '#6b7280';
  const inputBg = dark ? '#162a50' : '#fff';
  const inputBorder = dark ? 'rgba(255,255,255,0.15)' : '#e5e7eb';
  const dropBg = dark ? '#162a50' : '#fff5f5';
  const dropHoverBg = dark ? '#1e3460' : '#ffe4e6';
  const accent = '#f43f5e';

  return `
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;600;700&display=swap');
.shotgun-page *, .shotgun-page *::before, .shotgun-page *::after { box-sizing: border-box; }

.shotgun-page { font-family:'Barlow',sans-serif; background:${bg}; min-height:100vh; padding:0 0 80px; color:${text}; }
.shotgun-page .shotgun-breadcrumb { display:flex; align-items:center; gap:6px; padding:14px 32px; font-size:13px; color:${textSub}; border-bottom:1px solid ${cardBorder}; background:${cardBg}; }
.shotgun-page .shotgun-breadcrumb .bc-active { color:${accent}; font-weight:500; }
.shotgun-page .shotgun-breadcrumb .bc-sep { color:${textSub}; }
.shotgun-page .shotgun-content { max-width:960px; margin:0 auto; padding:28px 16px; display:flex; flex-direction:column; gap:20px; }
.shotgun-page .section-card { background:${cardBg}; border-radius:12px; border:1px solid ${cardBorder}; padding:28px 32px; box-shadow:${dark ? '0 4px 20px rgba(0,0,0,0.25)' : '0 1px 4px rgba(0,0,0,0.06)'}; }
.shotgun-page .section-header { display:flex; align-items:flex-start; gap:14px; margin-bottom:24px; }
.shotgun-page .section-header-text h3 { margin:0 0 3px; font-size:19px; font-weight:800; color:${dark ? '#f8fafc' : '#111827'}; }
.shotgun-page .section-header-text p { margin:0; font-size:13px; color:${textSub}; }
.shotgun-page .divider { height:1px; background:${cardBorder}; margin:20px 0; }
.shotgun-page .sub-label { font-size:13px; font-weight:800; text-transform:uppercase; letter-spacing:.8px; color:${accent}; margin-bottom:12px; padding-bottom:6px; border-bottom:1.5px solid ${dark ? 'rgba(251,113,133,0.2)' : '#ffe4e6'}; }
.shotgun-page .g2 { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
@media(max-width:640px){ .shotgun-page .g2 { grid-template-columns:1fr; } }
.shotgun-page .field { display:flex; flex-direction:column; gap:5px; }
.shotgun-page .field > label { font-size:13px; font-weight:700; text-transform:uppercase; letter-spacing:.5px; color:${dark ? '#8aaac8' : '#374151'}; }
.shotgun-page .field > label .req { color:#dc2626; margin-left:2px; }
.shotgun-page .field input, .shotgun-page textarea { padding:10px 14px; border:1.5px solid ${inputBorder}; border-radius:8px; font-size:14px; font-family:'Barlow',sans-serif; color:${text}; background:${inputBg}; outline:none; width:100%; transition:border-color .18s, box-shadow .18s; }
.shotgun-page .field input:focus, .shotgun-page textarea:focus { border-color:${accent}; box-shadow:0 0 0 3px rgba(251,113,133,.15); }
.shotgun-page .field input::placeholder { color:${dark ? '#475569' : '#c9cdd4'}; }
.shotgun-page .zip-drop-area { border:2px dashed ${dark ? 'rgba(255,255,255,0.2)' : '#fca5a5'}; border-radius:12px; padding:36px 20px; text-align:center; cursor:pointer; background:${dropBg}; position:relative; transition:all .18s; }
.shotgun-page .zip-drop-area:hover, .shotgun-page .zip-drop-area.drag { border-color:${accent}; background:${dropHoverBg}; }
.shotgun-page .zip-drop-area input[type=file] { position:absolute; inset:0; opacity:0; cursor:pointer; width:100%; height:100%; }
.shotgun-page .zip-icon-row { display:flex; align-items:center; justify-content:center; gap:8px; margin-bottom:10px; }
.shotgun-page .zip-drop-main { font-size:14px; font-weight:700; color:${accent}; margin-bottom:3px; }
.shotgun-page .zip-drop-hint { font-size:12px; color:${textSub}; }
.shotgun-page .zip-chip { display:flex; align-items:center; gap:10px; background:${dark ? 'rgba(251,113,133,0.15)' : '#ffe4e6'}; border:1.5px solid ${dark ? 'rgba(251,113,133,0.3)' : '#fca5a5'}; border-radius:10px; padding:10px 14px; margin-top:2px; }
.shotgun-page .zip-chip-info { flex:1; min-width:0; }
.shotgun-page .zip-chip-name { font-size:13px; font-weight:600; color:${accent}; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.shotgun-page .zip-chip-size { font-size:11px; color:${textSub}; margin-top:2px; }
.shotgun-page .zip-chip-remove { background:none; border:none; cursor:pointer; color:#f06060; font-size:16px; padding:4px; border-radius:4px; display:flex; align-items:center; }
.shotgun-page .zip-chip-remove:hover { background:rgba(240,96,96,0.15); }
.shotgun-page .txt-drop-area { border:2px dashed ${dark ? 'rgba(255,255,255,0.2)' : '#fca5a5'}; border-radius:10px; padding:18px 16px; text-align:center; cursor:pointer; background:${dropBg}; position:relative; transition:all .18s; }
.shotgun-page .txt-drop-area:hover, .shotgun-page .txt-drop-area.drag { border-color:${accent}; background:${dropHoverBg}; }
.shotgun-page .txt-drop-area input[type=file] { position:absolute; inset:0; opacity:0; cursor:pointer; width:100%; height:100%; }
.shotgun-page .txt-drop-main { font-size:13px; font-weight:600; color:${accent}; margin-top:6px; }
.shotgun-page .txt-drop-hint { font-size:11px; color:${textSub}; margin-top:2px; }
.shotgun-page .chip-list { display:flex; flex-wrap:wrap; gap:6px; margin-top:8px; }
.shotgun-page .chip { display:inline-flex; align-items:center; gap:5px; background:${dark ? 'rgba(251,113,133,0.15)' : '#ffe4e6'}; color:${accent}; border:1px solid ${dark ? 'rgba(251,113,133,0.3)' : '#fca5a5'}; border-radius:20px; padding:3px 10px 3px 8px; font-size:11px; font-weight:500; max-width:240px; }
.shotgun-page .chip-name { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.shotgun-page .chip button { background:none; border:none; cursor:pointer; color:${accent}; font-size:13px; line-height:1; padding:0; }
.shotgun-page .file-tree { background:${dark ? '#162a50' : '#f8fafc'}; border:1px solid ${cardBorder}; border-radius:10px; padding:16px; margin-bottom:20px; font-family:monospace; font-size:13px; }
.shotgun-page .ft-title { font-size:12px; font-weight:700; color:${textSub}; text-transform:uppercase; letter-spacing:.5px; margin-bottom:10px; font-family:'Barlow',sans-serif; }
.shotgun-page .ft-root { margin-bottom:4px; display:flex; align-items:center; gap:6px; }
.shotgun-page .ft-line { display:flex; align-items:center; padding:2px 0; }
.shotgun-page .ft-branch { color:${textSub}; margin-right:4px; }
.shotgun-page .ft-item { color:${text}; }
.shotgun-page .ft-item.highlight { color:${accent}; font-weight:700; }
.shotgun-page .method-area { width:100%; min-height:220px; border:1.5px solid ${inputBorder}; border-radius:8px; padding:12px; font-size:13px; font-family:sans-serif; line-height:1.6; color:${text}; background:${inputBg}; outline:none; }
.shotgun-page .method-meta { display:flex; justify-content:space-between; margin-top:6px; font-size:11px; color:${textSub}; }
.shotgun-page .btn-reset { background:none; border:none; color:${accent}; cursor:pointer; font-weight:600; }

.shotgun-page .counter { display:inline-flex; align-items:center; border:1.5px solid ${inputBorder}; border-radius:8px; overflow:hidden; }
.shotgun-page .counter button { width:32px; height:36px; background:${dark ? '#162a50' : '#f9fafb'}; border:none; cursor:pointer; font-size:16px; font-weight:700; color:${text}; display:flex; align-items:center; justify-content:center; transition:all .15s; }
.shotgun-page .counter button:hover { background:${dark ? 'rgba(251,113,133,0.2)' : '#ffe4e6'}; color:${accent}; }
.shotgun-page .counter span { width:44px; text-align:center; font-size:14px; font-weight:700; color:${text}; border-left:1px solid ${inputBorder}; border-right:1px solid ${inputBorder}; padding:8px 0; font-variant-numeric:tabular-nums; }

.shotgun-page .comp-num-badge { width:26px; height:26px; background:${accent}; color:${dark ? '#0a1628' : '#fff'}; border-radius:50%; font-size:11px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.shotgun-page .comp-title-row { display:flex; align-items:center; gap:10px; margin-bottom:10px; }

.submit-bar { position:fixed; bottom:0; left:0; right:0; background:#fff; border-top:1px solid #e5e7eb; padding:14px 32px; display:flex; align-items:center; justify-content:flex-end; gap:12px; box-shadow:0 -2px 12px rgba(0,0,0,0.07); z-index:100; }
.btn-submit { display:inline-flex; align-items:center; gap:8px; background:${accent}; color:${dark ? '#0a1628' : '#fff'}; border:none; border-radius:8px; padding:13px 32px; font-size:15px; font-weight:700; cursor:pointer; transition:all .18s; box-shadow:0 4px 14px rgba(251,113,133,.35); }
.btn-submit:hover:not(:disabled) { background:#f43f5e; transform:translateY(-1px); }
.btn-submit:disabled { opacity:.6; cursor:not-allowed; }
.submit-error { font-size:12px; font-weight:600; color:#f87171; background:${dark ? '#3c1818' : '#fee2e2'}; border:1px solid ${dark ? '#7f1d1d' : '#fca5a5'}; border-radius:7px; padding:7px 12px; }
.success-wrap { max-width:520px; margin:60px auto; text-align:center; background:${cardBg}; border-radius:14px; padding:48px 32px; border:1px solid ${cardBorder}; box-shadow:0 4px 20px rgba(0,0,0,.25); }
.success-wrap .si { font-size:52px; margin-bottom:16px; }
.success-wrap h2 { margin:0 0 8px; font-size:22px; color:${accent}; }
.success-wrap p { margin:0; font-size:14px; color:${textSub}; }
.btn-back { margin-top:24px; display:inline-block; background:${dark ? '#162a50' : '#e8f0fb'}; color:${accent}; border:1.5px solid ${dark ? 'rgba(251,113,133,0.3)' : '#b9d3ee'}; border-radius:8px; padding:10px 24px; font-size:13px; font-weight:600; cursor:pointer; font-family:'Barlow',sans-serif; }
.btn-open-report { display:inline-flex; align-items:center; gap:6px; margin-top:16px; margin-bottom:4px; background:${accent}; color:${dark ? '#0a1628' : '#fff'}; text-decoration:none; border-radius:8px; padding:10px 24px; font-size:13px; font-weight:600; font-family:'Barlow',sans-serif; box-shadow:0 3px 10px rgba(251,113,133,.25); }
`;
}

function fmtSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/* ══ Icons ══ */
function IconFolder({ size = 30 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M3 7c0-1.1.9-2 2-2h4l2 2h8c1.1 0 2 .9 2 2v8c0 1.1-.9 2-2 2H5c-1.1 0-2-.9-2-2V7z" fill="#f59e0b" stroke="#d97706" strokeWidth="1" />
    </svg>
  );
}

function IconArrow() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.5">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

function IconZip({ size = 30 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect x="4" y="2" width="16" height="20" rx="2" fill="#125AAC" stroke="#0d4a8f" strokeWidth="1" />
      <rect x="9" y="2" width="6" height="3" rx="1" fill="#0d4a8f" />
      <rect x="10" y="6" width="4" height="1.5" fill="#bcdcf7" />
      <rect x="10" y="9" width="4" height="1.5" fill="#bcdcf7" />
      <rect x="10" y="12" width="4" height="1.5" fill="#bcdcf7" />
      <text x="12" y="19" textAnchor="middle" fontSize="5" fontWeight="900" fill="white" fontFamily="monospace">ZIP</text>
    </svg>
  );
}

function IconTxt({ size = 26 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect x="4" y="2" width="16" height="20" rx="2" fill="#f3f4f6" stroke="#9ca3af" strokeWidth="1.2" />
      <line x1="8" y1="8" x2="16" y2="8" stroke="#6b7280" strokeWidth="1.4" />
      <line x1="8" y1="12" x2="16" y2="12" stroke="#6b7280" strokeWidth="1.4" />
      <line x1="8" y1="16" x2="13" y2="16" stroke="#6b7280" strokeWidth="1.4" />
    </svg>
  );
}

/* ══ ZIP Upload Zone ══ */
function ZipUploadZone({ label, hint, value, onChange }) {
  const [drag, setDrag] = useState(false);
  const handle = (files) => { if (files?.length) onChange(files[0]); };
  return (
    <div>
      {!value ? (
        <div className={`zip-drop-area${drag ? " drag" : ""}`}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); handle(e.dataTransfer.files); }}>
          <input type="file" accept=".zip" onChange={(e) => handle(e.target.files)} />
          <div className="zip-icon-row"><IconFolder size={30} /><IconArrow /><IconZip size={30} /></div>
          <div className="zip-drop-main">{label}</div>
          {hint && <div className="zip-drop-hint">{hint}</div>}
        </div>
      ) : (
        <div className="zip-chip">
          <IconZip size={32} />
          <div className="zip-chip-info">
            <div className="zip-chip-name">{value.name}</div>
            <div className="zip-chip-size">{fmtSize(value.size)}</div>
          </div>
          <button className="zip-chip-remove" onClick={() => onChange(null)}>✕</button>
        </div>
      )}
    </div>
  );
}

/* ══ TXT Upload Zone ══ */
function UploadZone({ label, hint, accept, value, onChange }) {
  const [drag, setDrag] = useState(false);
  const handle = (files) => { if (files?.length) onChange(files[0]); };
  return (
    <div>
      <div className={`txt-drop-area${drag ? " drag" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); handle(e.dataTransfer.files); }}>
        <input type="file" accept={accept} onChange={(e) => handle(e.target.files)} />
        <IconTxt size={26} />
        <div className="txt-drop-main">{label}</div>
        {hint && <div className="txt-drop-hint">{hint}</div>}
      </div>
      {value && (
        <div className="chip-list">
          <div className="chip"><span>📄 {value.name}</span><button onClick={() => onChange(null)}>✕</button></div>
        </div>
      )}
    </div>
  );
}

/* ══ Text Field ══ */
function Field({ label, req, placeholder, value, onChange, type = "text" }) {
  return (
    <div className="field">
      <label>{label}{req && <span className="req">*</span>}</label>
      <input type={type} placeholder={placeholder || ""} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

/* ══ File Tree ══ */
function FileTree({ title, lines }) {
  return (
    <div className="file-tree">
      <div className="ft-title">{title}</div>
      {lines.map((l, i) => {
        if (l.type === "root") return (
          <div key={i} className="ft-root"><span>📁</span><span style={{ color: "#2563eb", fontWeight: 700 }}>{l.text}</span></div>
        );
        return (
          <div key={i} className="ft-line">
            <span className="ft-branch">{l.branch}</span>
            <span className={`ft-item ${l.cls || ""}`}>{l.text}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
export default function ShotgunReportUpload({ setReportUrls, setActiveTab, dark }) {
  React.useEffect(() => {
    injectStyle(dark);
    return () => {
      const s = document.getElementById("shotgun-style");
      if (s) s.remove();
    };
  }, [dark]);

  const [loading, setLoading] = useState(false);
  const [actionType, setActionType] = useState("html");
  const [generatedReport, setGeneratedReport] = useState(null);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  /* Section 01 — Client info */
  const [s1, setS1] = useState({
    reportTitle: "", clientName: "", institution: "",
    projectId: "", salesPerson: "", contactEmail: "",
  });
  const u1 = (k) => (v) => setS1((p) => ({ ...p, [k]: v }));

  /* Section 02 — Project info */
  const [sampleFile, setSampleFile] = useState(null);
  const [numComp, setNumComp] = useState(1);
  const [compTitles, setCompTitles] = useState([""]);

  const setNumCompSafe = (n) => {
    const count = Math.max(1, Math.min(30, n));
    const titles = [...compTitles];
    while (titles.length < count) titles.push("");
    setNumComp(count);
    setCompTitles(titles.slice(0, count));
  };
  const setCompTitle = (i, v) => {
    const t = [...compTitles]; t[i] = v; setCompTitles(t);
  };

  /* Section 03 — QC.zip */
  const [qcZip, setQcZip] = useState(null);

  /* Section 04 — Comparisons.zip */
  const [comparisonsZip, setComparisonsZip] = useState(null);

  /* Optional Modules — Classified Reads (Taxonomy Summary) & KEGG (Functional Prediction) */
  const [classifiedReadsFile, setClassifiedReadsFile] = useState(null);
  const [keggFile, setKeggFile] = useState(null);

  /* Section 05 — Resources / Deliverables ZIP */
  const [resourcesZip, setResourcesZip] = useState(null);

  /* Section 06 — Methodology */
  const [methodologyText, setMethodologyText] = useState(DEFAULT_METHODOLOGY);

     async function submitReport(format = "html") {
    setActionType(format);
    setError("");
    setSuccess(false);

    // Fast path: if report was already generated and user clicks "Export PDF"
    if (format === "pdf" && generatedReport?.pdfFilename) {
      setLoading(true);
      try {
        const dlLink = document.createElement("a");
        dlLink.href = `/api/shotgun/download-report?file=${generatedReport.pdfFilename}`;
        dlLink.download = generatedReport.pdfFilename;
        document.body.appendChild(dlLink);
        dlLink.click();
        setTimeout(() => {
          if (document.body.contains(dlLink)) document.body.removeChild(dlLink);
        }, 500);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);

    const form = new FormData();
    form.append("clientInfo", JSON.stringify(s1));
    form.append("compTitles", JSON.stringify((compTitles || []).filter(t => t && t.trim())));
    form.append("methodologyText", methodologyText);
    if (sampleFile) form.append("sampleFile", sampleFile);
    if (qcZip) form.append("qcZip", qcZip);
    if (comparisonsZip) form.append("comparisonsZip", comparisonsZip);
    if (classifiedReadsFile) form.append("classifiedReadsFile", classifiedReadsFile);
    if (keggFile) form.append("keggFile", keggFile);
    if (resourcesZip) form.append("resourcesZip", resourcesZip);

    try {
      const res = await fetch("/api/shotgun/upload-shotgun", { method: "POST", body: form });
      const contentType = res.headers.get("content-type");
      let data = null;

      if (contentType && contentType.includes("application/json")) {
        data = await res.json();
      } else {
        const text = await res.text();
        const cleanMsg = text.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
        throw new Error(cleanMsg.slice(0, 200) || `Server error (${res.status} ${res.statusText})`);
      }

      if (!res.ok) throw new Error(data.error || data.detail || "Server error — check backend logs");

      const downloadFilename = data.filename || `${s1.projectId || "Shotgun_Report"}.html`;
      const pdfFilename = data.pdfFilename || downloadFilename.replace(/\.html$/, ".pdf");
      const fullReportUrl = data.reportUrl;
      const fullPdfUrl = data.pdfUrl || `/reports/${pdfFilename}`;

      setGeneratedReport({
        htmlUrl: data.reportUrl,
        fullHtmlUrl: fullReportUrl,
        pdfUrl: data.pdfUrl || `/reports/${pdfFilename}`,
        fullPdfUrl,
        filename: downloadFilename,
        pdfFilename,
      });
      setSuccess(true);

      if (format === "pdf") {
        const dlLink = document.createElement("a");
        dlLink.href = `/api/shotgun/download-report?file=${pdfFilename}`;
        dlLink.download = pdfFilename;
        document.body.appendChild(dlLink);
        dlLink.click();
        setTimeout(() => {
          if (document.body.contains(dlLink)) document.body.removeChild(dlLink);
        }, 500);
      } else {
        const dlLink = document.createElement("a");
        dlLink.href = `/api/shotgun/download-report?file=${downloadFilename}`;
        dlLink.download = downloadFilename;
        document.body.appendChild(dlLink);
        dlLink.click();
        setTimeout(() => {
          if (document.body.contains(dlLink)) document.body.removeChild(dlLink);
        }, 500);

        window.open(fullReportUrl, "_blank");
      }

      if (setReportUrls) {
        setReportUrls({
          html: fullReportUrl,
          qcExcel: "",
          mappingExcel: "",
          variantExcel: "",
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }
  

  const wordCount = methodologyText.trim().split(/\s+/).filter(Boolean).length;

  return (
    <div className="shotgun-page">
      <div className="shotgun-content">

        {/* ══ 01 CLIENT INFO ══ */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text"><h3>Client &amp; Report Info</h3><p>Appears on the report cover page</p></div>
          </div>
          <div className="field" style={{ marginBottom: 16 }}>
            <label>REPORT TITLE <span className="req">*</span></label>
            <input type="text" placeholder="Shotgun Metagenomics Sequencing Report" value={s1.reportTitle} onChange={(e) => u1("reportTitle")(e.target.value)} />
          </div>
          <div className="g2" style={{ marginBottom: 16 }}>
            <Field label="CLIENT NAME" req placeholder="e.g. Mr. Pasupathi" value={s1.clientName} onChange={u1("clientName")} />
            <Field label="INSTITUTION" placeholder="e.g. BDU" value={s1.institution} onChange={u1("institution")} />
          </div>
          <div className="g2" style={{ marginBottom: 16 }}>
            <Field label="PROJECT ID" req placeholder="e.g. OPL-SHOTGUN-25008" value={s1.projectId} onChange={u1("projectId")} />
            <Field label="SALESPERSON" placeholder="e.g. Gowri Shankar J" value={s1.salesPerson} onChange={u1("salesPerson")} />
          </div>
          <Field label="CONTACT EMAIL" req type="email" placeholder="client@email.com" value={s1.contactEmail} onChange={u1("contactEmail")} />
        </div>

        {/* ══ 02 PROJECT INFO ══ */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text"><h3>Project Information</h3><p>Sample list and comparison groups</p></div>
          </div>

          <div className="g2">
            <div>
              <div className="sub-label">Sample / Condition File</div>
              <UploadZone label="Drop Sample_info.txt here" hint="Tab-separated · Sample ID · Condition" accept=".txt,.tsv" value={sampleFile} onChange={setSampleFile} />
              <div className="info-banner" style={{ marginTop: 12, marginBottom: 0 }}>
                <div className="info-banner-icon">📋</div>
                <div className="info-banner-text">Format: <code>Sample&nbsp;&nbsp;Condition</code> — e.g. <code>CFM-R1&nbsp;&nbsp;Treatment</code></div>
              </div>
            </div>
            <div>
              <div className="sub-label">Comparison Groups</div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px", color: "#374151", display: "block", marginBottom: 6 }}>NUMBER OF COMPARISONS</label>
                <div className="counter">
                  <button type="button" onClick={() => setNumCompSafe(numComp - 1)}>−</button>
                  <span>{numComp}</span>
                  <button type="button" onClick={() => setNumCompSafe(numComp + 1)}>+</button>
                </div>
              </div>
              {compTitles.map((t, i) => (
                <div key={i} className="comp-title-row">
                  <div className="comp-num-badge">{i + 1}</div>
                  <input type="text" placeholder="e.g. CFM vs CFUM" value={t} onChange={(e) => setCompTitle(i, e.target.value)}
                    style={{ flex: 1, padding: "9px 12px", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 13, fontFamily: "'Barlow', sans-serif", outline: "none", color: "#111827", background: "#fff" }}
                    onFocus={(e) => { e.target.style.borderColor = "#fb7185"; e.target.style.boxShadow = "0 0 0 3px rgba(251,113,133,.15)"; }}
                    onBlur={(e) => { e.target.style.borderColor = "#e5e7eb"; e.target.style.boxShadow = "none"; }} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ══ 03 QC ZIP ══ */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text"><h3>Quality Control (QC)</h3><p>Upload QC.zip — FastQC/MultiQC stats &amp; quality image plots</p></div>
          </div>
          <div className="info-banner">
            <div className="info-banner-icon">ℹ️</div>
            <div className="info-banner-text">Compress the <strong>QC/</strong> folder directly as <strong>QC.zip</strong>, keeping the exact file names below.</div>
          </div>
          <ZipUploadZone
            label="Drop QC.zip, .tsv, or .txt here or click to browse"
            hint="QC.zip (.zip) or QC summary table (.tsv / .txt) — accepts both .tsv and .txt files"
            accept=".zip,.tsv,.txt,.csv"
            value={qcZip}
            onChange={setQcZip}
          />
          <FileTree title="QC.zip — exact file names expected (.tsv or .txt accepted)" lines={[
            { type: "root", text: "QC/" },
            { branch: "├── ", text: "multiqc_general_stats_raw.txt (or .tsv)", cls: "green" },
            { branch: "├── ", text: "multiqc_general_stats_trim.txt (or .tsv)", cls: "green" },
            { branch: "├── ", text: "fastqc_per_sequence_quality_scores_plot_raw.tsv (or .txt)", cls: "green" },
            { branch: "├── ", text: "fastqc_per_sequence_quality_scores_plot_trim.tsv (or .txt)", cls: "green" },
            { branch: "├── ", text: "fastqc_sequence_counts_plot_raw.tsv (or .txt)", cls: "green" },
            { branch: "├── ", text: "fastqc_sequence_counts_plot_trim.tsv (or .txt)", cls: "green" },
            { branch: "├── ", text: "fastqc_per_sequence_gc_content_plot_raw.png", cls: "purple" },
            { branch: "├── ", text: "fastqc_per_sequence_gc_content_plot_trim.png", cls: "purple" },
            { branch: "├── ", text: "fastqc_per_base_sequence_quality_plot_raw.png", cls: "purple" },
            { branch: "└── ", text: "fastqc_per_base_sequence_quality_plot_trim.png", cls: "purple" },
          ]} />
        </div>

        {/* ══ 04 COMPARISONS ZIP ══ */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text"><h3>Comparison Results</h3><p>Upload Comparisons.zip containing subfolders for each comparison group</p></div>
          </div>
          <div className="info-banner">
            <div className="info-banner-icon">ℹ️</div>
            <div className="info-banner-text">
              Compress comparison folders into a single <strong>Comparisons.zip</strong>.
              Each subfolder contains domain folders: <strong>Archaea/, Bacteria/, Eukaryotes/, Fungi/, Viruses/</strong>.
              (Classified reads and KEGG are now separate uploads below, but existing ZIPs with them inside remain supported).
            </div>
          </div>
          <ZipUploadZone label="Drop Comparisons.zip here or click to browse" hint="One ZIP containing ALL comparison folders" value={comparisonsZip} onChange={setComparisonsZip} />

          {(() => {
            const lines = [{ type: "root", text: "Comparisons/" }];
            const titles = compTitles.length ? compTitles : [""];
            titles.forEach((t, i) => {
              const lbl = t.trim() || `Comparison_${i + 1}`;
              const isLast = i === titles.length - 1;
              const pre = isLast ? "└── " : "├── ";
              const pipe = isLast ? "    " : "│   ";
              lines.push({ branch: pre, text: `${lbl}/`, cls: "folder" });
              ["Archaea", "Bacteria", "Eukaryotes", "Fungi", "Viruses"].forEach((d, di) => {
                const dLast = di === 4;
                lines.push({ branch: `${pipe}${dLast ? "└── " : "├── "}`, text: `${d}/`, cls: "folder" });
                lines.push({ branch: `${pipe}    ├── `, text: "Abundance/  (Abundance_<Level>.jpeg/.pdf + <Level>_abundance.xlsx)", cls: "green" });
                lines.push({ branch: `${pipe}    ├── `, text: "Diversity/  (Alpha/Beta plots [.png/.jpg], Alpha/Beta distance tables [.xlsx/.csv])", cls: "green" });
                lines.push({ branch: `${pipe}    ├── `, text: "Heatmaps/   (<Level>_Heatmap.png/.pdf)", cls: "green" });
                lines.push({ branch: `${pipe}    ├── `, text: "Kraken/     (<sample>.report)", cls: "orange" });
                lines.push({ branch: `${pipe}    ├── `, text: "Krona/      (<sample>_krona.txt + Krona.html)", cls: "purple" });
                lines.push({ branch: `${pipe}    ├── `, text: "Plots/      (sankey_<sample>.jpg/.pdf)", cls: "pink" });
                lines.push({ branch: `${pipe}    ├── `, text: "<comparison>_<domain>.biom", cls: "orange" });
                lines.push({ branch: `${pipe}    └── `, text: "Taxonomy.xlsx (or .csv)", cls: "orange" });
              });
              lines.push({ branch: `${pipe}├── `, text: "KEGG/  (optional or upload separately below)", cls: "purple" });
              lines.push({ branch: `${pipe}└── `, text: "Classified_reads.xlsx (optional or upload separately below)", cls: "orange" });
            });
            return <FileTree title="Comparisons.zip — expected structure" lines={lines} />;
          })()}
        </div>
      
      {/* ══ 05 OPTIONAL MODULES: TAXONOMY SUMMARY & FUNCTIONAL PREDICTION ══ */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text">
              <h3>Taxonomy Summary &amp; Functional Prediction (Optional)</h3>
              <p>Upload Classified Reads table and KEGG Functional Prediction data separately. If not uploaded, these tabs will be omitted from the report.</p>
            </div>
          </div>
          <div className="info-banner">
            <div className="info-banner-icon">ℹ️</div>
            <div className="info-banner-text">
              These files are separate uploads. If your dataset does not have classified reads or KEGG pathway data, leave them empty to exclude those tabs from the report.
            </div>
          </div>

          <div className="g2">
            <div>
              <div className="sub-label">Taxonomy Summary (Classified Reads)</div>
              <UploadZone
                label="Drop Classified_reads.xlsx, .csv, .tsv, .txt or .zip here"
                hint="Excel (.xlsx), CSV, TSV, TXT table of classified read counts, or ZIP (optional)"
                accept=".xlsx,.xls,.csv,.tsv,.txt,.zip"
                value={classifiedReadsFile}
                onChange={setClassifiedReadsFile}
              />
            </div>
            <div>
              <div className="sub-label">Functional Prediction (KEGG)</div>
              <UploadZone
                label="Drop KEGG.zip or KEGG Data here"
                hint="ZIP archive, Excel (.xlsx/.xls), CSV/TSV table, or KEGG pathway plot (.png/.jpg) (optional)"
                accept=".zip,.xlsx,.xls,.csv,.tsv,.txt,.png,.jpg,.jpeg,.svg,.webp"
                value={keggFile}
                onChange={setKeggFile}
              />
            </div>
          </div>
        </div>
    

        {/* ══ 06 DATA FILES & DELIVERABLES (RESOURCES TAB) ══ */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text">
              <h3>Data Files &amp; Deliverables (Resources Tab)</h3>
              <p>Upload a single ZIP containing only the important files you want listed in the Resources → Data Files &amp; Deliverables section of the report</p>
            </div>
          </div>
          <div className="info-banner">
            <div className="info-banner-icon">💡</div>
            <div className="info-banner-text">
              <strong>Tip:</strong> Put only final deliverables inside this ZIP (e.g. final taxonomy tables, KEGG functional matrices, classified reads summaries, high-res diversity plots). Intermediate FastQC plots and raw MultiQC files will not appear automatically.
            </div>
          </div>
          <ZipUploadZone
            label="Drop Deliverables / Resources ZIP here or click to browse"
            hint="One ZIP containing final deliverables (Taxonomy, KEGG, plots, etc.)"
            value={resourcesZip}
            onChange={setResourcesZip}
          />
        </div>

        {/* ══ 07 METHODOLOGY ══ */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text"><h3>Materials &amp; Methods</h3><p>Editable methodology text rendered in Section 2 of the final report</p></div>
          </div>
          <div className="info-banner">
            <div className="info-banner-icon">📋</div>
            <div className="info-banner-text">Pre-filled from your standard shotgun metagenomics protocol. Edit freely.</div>
          </div>
          <div style={{ position: "relative" }}>
            <textarea value={methodologyText} onChange={(e) => setMethodologyText(e.target.value)} className="method-area"
              placeholder="Enter your methodology text here…" spellCheck={true} />
            <div className="method-meta">
              <span className="method-word-count">{wordCount.toLocaleString()} words · {methodologyText.length.toLocaleString()} characters</span>
              <button className="method-clear" onClick={() => setMethodologyText("")} title="Clear all text">✕ Clear</button>
            </div>
          </div>
        </div>

      </div>

      {/* ══ SUBMIT BAR ══ */}
      <div className="submit-bar">
        {error && <div className="submit-error">⚠ {error}</div>}
        {success && generatedReport && (
          <div className="submit-success" style={{ display: "flex", alignItems: "center", gap: 10, background: "#dcfce7", color: "#15803d", padding: "10px 16px", borderRadius: 8, fontWeight: 600, fontSize: 13 }}>
            <span>✓ Report Generated!</span>
            <a
              href={generatedReport.fullHtmlUrl}
              target="_blank"
              rel="noreferrer"
              style={{ color: "#15803d", fontWeight: 700, textDecoration: "underline" }}
            >
              Open HTML ↗
            </a>
            <button
              type="button"
              onClick={() => submitReport("pdf")}
              style={{
                background: "none",
                border: "none",
                color: "#15803d",
                fontWeight: 700,
                textDecoration: "underline",
                cursor: "pointer",
                padding: 0
              }}
            >
              Download PDF 📄
            </button>
          </div>
        )}
        <button className="btn-submit" onClick={() => submitReport("html")} disabled={loading}>
          {loading && actionType === "html" ? (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" style={{ animation: "shotgun-spin 0.9s linear infinite" }}>
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
              </svg>
              Generating Report…
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
              Upload &amp; Generate Report
            </>
          )}
        </button>

        <button
          className="btn-submit"
          onClick={() => submitReport("pdf")}
          disabled={loading}
          style={{ background: "#e11d48", boxShadow: "0 4px 14px rgba(225,29,72,0.3)" }}
        >
          {loading && actionType === "pdf" ? (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" style={{ animation: "shotgun-spin 0.9s linear infinite" }}>
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
              </svg>
              Exporting PDF…
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10 9 9 9 8 9"/>
              </svg>
              Export PDF Report
            </>
          )}
        </button>
      </div>
    </div>
  );
}